import React, { useEffect, useState } from "react";
import { XStack, YStack, Text, Button, Circle, ScrollView } from "tamagui";
import { useUser, IdentityContext } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import api, { handleRequest } from "@services/axios/apiManager";
import { colors } from "@styles/colors";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";
import { getFirstText } from "@utils/func/textTool";
import { AxiosResponse } from "axios";
import InlineLoading from "@components/loading/InlineLoading";

interface OrderListHeaderProps {
  filteredCount?: number;
}

const OrderListHeader = ({ filteredCount = 0 }: OrderListHeaderProps) => {
  // 淡霧頁首：半透明白 chips 在淡底上看不見，改成白卡（選中＝淡藍底 + 深藍字）
  const haze = useHazePage();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const [identityList, setIdentityList] = useState<IdentityContext[]>([]);
  const [loading, setLoading] = useState(false);
  const { cusJwtToken } = useCusAuth();

  useEffect(() => {
    if (cusJwtToken) {
      setLoading(true);
      handleRequest({
        asyncRequest: () =>
          api.get("/app-api/customer/contexts", {
            headers: { Authorization: `Bearer ${cusJwtToken}` },
          }),
        successCallback: (res: AxiosResponse) => {
          const { data } = res.data;
          if (data?.contexts?.length > 0) {
            setIdentityList(data.contexts);
          }
        },
        finalCallback: () => {
          setLoading(false);
        },
      });
    }
  }, [cusJwtToken]);

  // v0.7：「全部」是 synthetic sentinel — 沿用 ALL_CONTEXT_ID 作 key/比對；點到時 setChooseIdentity(null)。
  const ALL_CONTEXT_ID = -1 as const;
  type IdentityTab = { context_id: number; context_name: string } | IdentityContext;
  /* 「全部帳戶」而不是「全部」：底下的狀態分頁籤第一項也叫「全部」，兩排同一個詞上下疊著，
     但一個是膠囊（文字被內距推進去 12px）、一個是純文字（切齊左邊界），看起來就像沒對齊。
     兩者本來就是不同維度的篩選（哪個帳戶 / 什麼狀態），名字講清楚就不會互相干擾。 */
  const identities: IdentityTab[] = [{ context_id: ALL_CONTEXT_ID, context_name: "全部帳戶" }, ...identityList];

  const handleSelect = (identity: IdentityTab) => {
    if (identity.context_id === ALL_CONTEXT_ID) {
      setChooseIdentity(null);
    } else {
      setChooseIdentity(identity as IdentityContext);
    }
  };

  return (
    <YStack gap="$3" width="100%">
      {/* 膠囊外框切齊內容左邊界（與標題、分頁籤、白卡同一條線）。
          曾試過整排往左推 13px 讓膠囊的「字」對齊底下的分頁籤，結果膠囊貼到螢幕邊、更難看，
          已改回框線對齊；兩排文字的 13px 落差改用命名區分（「全部帳戶」vs「全部」）。 */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} width="100%">
        <XStack gap="$2" pb="$1" ai="center">
          {loading ? (
            <XStack px="$4">
              <InlineLoading />
            </XStack>
          ) : (
            identities.map((item) => {
              const isAll = item.context_id === ALL_CONTEXT_ID;
              const isSelected = isAll ? !chooseIdentity : chooseIdentity?.context_id === item.context_id;
              const name = item.context_name || "";

              return (
                <Button
                  key={item.context_id}
                  onPress={() => handleSelect(item)}
                  br={20}
                  px="$3"
                  h={36}
                  bg={haze ? (isSelected ? v2.primarySoft : "#fff") : isSelected ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)"}
                  borderColor={haze ? (isSelected ? v2.primary : v2.cardBorder) : isSelected ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.25)"}
                  /* 淡霧底下選中與否只靠邊線與底色分辨，粗細一律 1.5——原本選中加粗到 2 會讓
                     膠囊寬度跳一下，且在淺底上那圈粗邊會讀成灰框。 */
                  borderWidth={haze ? 1.5 : isSelected ? 2 : 1.5}
                  hoverStyle={{ backgroundColor: haze ? "#f7fbff" : "rgba(255,255,255,0.2)" }}
                  pressStyle={{ backgroundColor: haze ? "#f2f7fd" : "rgba(255,255,255,0.3)" }}>
                  <XStack ai="center" gap="$2">
                    {!isAll && (
                      <Circle size={18} br={5} bg={haze ? (isSelected ? "#fff" : v2.chipBg) : isSelected ? "#fff" : "rgba(255,255,255,0.2)"} ai="center" jc="center">
                        <Text color={haze ? (isSelected ? v2.hazeAcc : v2.hazeSub) : isSelected ? colors.primary : "#fff"} fontSize={9} fow="700">
                          {getFirstText(name)}
                        </Text>
                      </Circle>
                    )}
                    <Text color={haze ? (isSelected ? v2.hazeInk : v2.hazeSub) : "#fff"} fontSize={12} fow={isSelected ? "700" : "500"}>
                      {name}
                    </Text>
                  </XStack>
                </Button>
              );
            })
          )}
        </XStack>
      </ScrollView>
    </YStack>
  );
};

export default OrderListHeader;
